import { useState } from "react";
import { MoveHorizontal } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";

const COMPARISONS = [
    {
        antes: "/photos/antes-sofa.jpg",
        depois: "/photos/depois-sofa.jpg",
        altAntes: "Sofá antes da higienização profissional da Brandão Morais",
        altDepois: "Sofá depois da higienização profissional da Brandão Morais",
        caption: "Higienização de sofá",
    },
    {
        antes: "/photos/antes-sofa-azul.jpg",
        depois: "/photos/depois-sofa-azul.jpg",
        altAntes: "Sofá claro antes da higienização profissional",
        altDepois: "Sofá claro depois da higienização profissional",
        caption: "Higienização de sofá claro",
    },
    {
        antes: "/photos/antes-colchao.jpg",
        depois: "/photos/depois-colchao.jpg",
        altAntes: "Colchão antes da higienização profissional da Brandão Morais",
        altDepois: "Colchão depois da higienização profissional da Brandão Morais",
        caption: "Higienização de colchão",
    },
    {
        antes: "/photos/antes-sofa-bege.jpg",
        depois: "/photos/depois-sofa-bege.jpg",
        altAntes: "Sofá bege antes da higienização profissional",
        altDepois: "Sofá bege depois da higienização profissional",
        caption: "Higienização de sofá com chaise",
    },
    {
        antes: "/photos/antes-colchao-2.jpg",
        depois: "/photos/depois-colchao-2.jpg",
        altAntes: "Colchão branco antes da higienização profissional",
        altDepois: "Colchão branco depois da higienização profissional",
        caption: "Higienização de colchão branco",
    },
    {
        antes: "/photos/antes-sofa-retratil.jpg",
        depois: "/photos/depois-sofa-retratil.jpg",
        altAntes: "Sofá retrátil antes da higienização profissional",
        altDepois: "Sofá retrátil depois da higienização profissional",
        caption: "Higienização de sofá retrátil",
    },
    {
        antes: "/photos/antes-cadeiras.jpg",
        depois: "/photos/depois-cadeiras.jpg",
        altAntes: "Cadeiras estofadas antes da higienização profissional",
        altDepois: "Cadeiras estofadas depois da higienização profissional",
        caption: "Higienização de cadeiras",
    },
    {
        antes: "/photos/antes-bancos-carro.jpg",
        depois: "/photos/depois-bancos-carro.jpg",
        altAntes: "Bancos de carro antes da higienização profissional",
        altDepois: "Bancos de carro depois da higienização profissional",
        caption: "Higienização de bancos automotivos",
    },
    {
        antes: "/photos/antes-tapete.jpg",
        depois: "/photos/depois-tapete.jpg",
        altAntes: "Tapete antes da higienização profissional",
        altDepois: "Tapete depois da higienização profissional",
        caption: "Higienização de tapete",
    },
];

const ComparisonSlider = ({ item, index }) => {
    const [pos, setPos] = useState(50);
    return (
        <div>
            <div
                data-testid={index === 0 ? "before-after-slider" : `before-after-slider-${index}`}
                className="relative rounded-3xl overflow-hidden border border-white/10 select-none"
            >
                <img
                    src={item.antes}
                    alt={item.altAntes}
                    className="w-full h-[300px] sm:h-[420px] object-cover"
                    draggable={false}
                    loading="lazy"
                />
                <img
                    src={item.depois}
                    alt={item.altDepois}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
                    draggable={false}
                    loading="lazy"
                />
                <span className="absolute top-5 left-5 bg-navy/80 backdrop-blur text-white text-xs font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-full">
                    Antes
                </span>
                <span className="absolute top-5 right-5 bg-brand text-white text-xs font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-full">
                    Depois
                </span>
                <div
                    className="absolute top-0 bottom-0 w-0.5 bg-white"
                    style={{ left: `${pos}%` }}
                    aria-hidden="true"
                >
                    <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-brand border-4 border-white flex items-center justify-center shadow-xl">
                        <MoveHorizontal className="w-5 h-5 text-white" />
                    </span>
                </div>
                <input
                    data-testid={index === 0 ? "before-after-range" : `before-after-range-${index}`}
                    type="range"
                    min="0"
                    max="100"
                    value={pos}
                    onChange={(e) => setPos(Number(e.target.value))}
                    aria-label={`Controlar comparação antes e depois — ${item.caption}`}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
                />
            </div>
            <p className="mt-4 text-center text-xs text-white/50 tracking-wide">
                Resultado real: {item.caption} — Brandão Morais Higienização.
            </p>
        </div>
    );
};

export const BeforeAfter = () => (
    <section
        id="antes-depois"
        data-testid="before-after-section"
        className="bg-navy noise-bg py-24 lg:py-32"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center max-w-2xl mx-auto mb-14">
                <Reveal>
                    <SectionLabel light>Antes e depois</SectionLabel>
                    <h2 className="mt-5 font-display font-bold text-white tracking-tight text-4xl sm:text-5xl leading-[1.1]">
                        O resultado que a higienização{" "}
                        <span className="text-brand">entrega</span>
                    </h2>
                    <p className="mt-5 text-white/60 text-base leading-relaxed">
                        Arraste o controle e veja a diferença de uma higienização profissional.
                    </p>
                </Reveal>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
                {COMPARISONS.map((item, i) => (
                    <Reveal key={item.caption} delay={i * 0.12}>
                        <ComparisonSlider item={item} index={i} />
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);
